import { each, filter, find, includes, isEmpty, map, reduce, uniq } from 'lodash';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ISubscription } from 'rxjs/Subscription';
import { ISdcStore, IStoreScope } from 'src/app/sdc/flux/sdc-store.interface';
import { StoreListenerUtils } from 'src/app/shared/services/store-listener-utils/store-listener-utils.service';
import { ImStudentSupport } from 'Src/app/shared/static-models/im-models/im-student-support.service';
import { IStudentSupport } from 'Src/ng2/shared/typings/interfaces/student-support.interface';
import { SupportStatuses } from '../../../../ng2/shared/constants/support-statuses.constant';
import { IAttendanceRecord } from '../../../../ng2/shared/typings/interfaces/attendance-record.interface';
import { ISchoolSupport } from '../../../../ng2/shared/typings/interfaces/support.interface';
import { ImSupport } from '../../../shared/static-models/im-models/im-support.service';
import { DateHelpers } from './../../../../ng2/shared/services/date-helpers/date-helpers.service';

export class AbsentStudentsListCtrl {
  static $inject = [
    '$scope',
    'AbsentStudentsListExporter',
    'SpinnerModal',
    'SDCStore',
    'StoreListenerUtils',
    'DateHelpers',
    'ImSupport',
    'ImStudentSupport',
  ];

  constructor(
    public $scope: IStoreScope,
    private AbsentStudentsListExporter,
    private SpinnerModal,
    private SDCStore: ISdcStore,
    private storeListenerUtils: StoreListenerUtils,
    private dateHelpers: DateHelpers,
    private imSupport: ImSupport,
    private imStudentSupport: ImStudentSupport,
  ) {}

  // bindings
  attendanceRecords;
  studentSupports$: Observable<IStudentSupport[]>;

  // init
  checkboxFilters;
  startDate: Date;
  endDate: Date;
  uniqueDates;
  table: any;
  supportsToDisplay: ISchoolSupport[];

  sortType: string;
  sortReverse: boolean;
  searchText: string;
  selectedSupport: any;
  searchSupport: any;

  // School Supports FrontEnt Status
  private supportsGroupedByFrontEndStatus: { [key: string]: ISchoolSupport[] };
  public supportsInProgress: ISchoolSupport[];
  public supportsCompleted: ISchoolSupport[];
  private selectedSupportsFrontEndStatus: string;
  public supportsGroupedByFrontEndStatusKeys: string[];
  public supportsFrontEndStatusHumanReadable: { [key: string]: string };
  public filteredSchoolSupports: ISchoolSupport[];
  public filteredAttendanceRecords: IAttendanceRecord[];

  studentSupportsSubscription: ISubscription;
  studentSupports: IStudentSupport[];
  public destroy$: Subject<boolean> = new Subject<boolean>();

  $onInit(): void {
    this.storeListenerUtils.startListeningAndLoad(this, [['schoolSupports']]);
  }

  public setUi(): void {
    this.studentSupportsSubscription = this.studentSupports$
      .pipe(
        tap((studentSupports: IStudentSupport[]) => {
          this.studentSupports = studentSupports;
          this.setView();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  setView(): void {
    // Default Start Date to 7 days ago (CM)
    if (!this.startDate) {
      this.startDate = this.dateHelpers
        .getMomentObj()
        .subtract(7, 'days')
        .toDate();
    }

    // Default End Date to today (CM)
    if (!this.endDate) this.endDate = new Date();

    this.checkboxFilters = {
      absentStatus: {
        bool: true,
        value: 'ABSENT',
      },
      lateStatus: {
        bool: true,
        value: 'LATE',
      },
      presentStatus: {
        bool: true,
        value: 'PRESENT',
      },
    };

    // table sorting:
    this.sortType = 'attendanceStatus'; // set the default sort type
    this.sortReverse = false; // set the default sort order

    // table filtering:
    this.searchText = '';
    this.selectedSupport = null;
    this.searchSupport = {};

    // set initial school supports
    const schoolSupports = this.SDCStore.getAllSchoolSupports();
    this.filteredAttendanceRecords = this.filterAttendanceRecordsByDateRange({
      attendanceRecords: this.attendanceRecords,
      startDate: this.startDate,
      endDate: this.endDate,
    });
    // if at least one attendance record for date range exists for support, then display in dropdown
    this.filteredSchoolSupports = reduce(
      schoolSupports,
      (acc, schoolSupport) => {
        const supportId = schoolSupport._id;
        const attendanceRecordExistsForSupport = this.filteredAttendanceRecords.some(attendanceRecord => {
          return attendanceRecord.supportId === supportId;
        });

        if (attendanceRecordExistsForSupport) acc.push(schoolSupport);

        return acc;
      },
      [],
    );
    this.supportsGroupedByFrontEndStatus = this.imSupport.getSupportsGroupedByFrontEndStatus(
      this.filteredSchoolSupports,
    );

    // Group school supports by frontend status (CM)
    this.supportsInProgress = this.supportsGroupedByFrontEndStatus[SupportStatuses.frontend.IN_PROGRESS] || [];
    this.supportsCompleted = this.supportsGroupedByFrontEndStatus[SupportStatuses.frontend.COMPLETED] || [];
    // Get human readable front end statuses for school supports (CM)
    this.supportsFrontEndStatusHumanReadable = SupportStatuses.frontendHumanReadable;
    this.supportsGroupedByFrontEndStatusKeys = []; // used to determined keys in 'Status' dropdown (CM)

    // Front end status options in dropdown
    if (this.supportsInProgress.length) {
      this.supportsGroupedByFrontEndStatusKeys.push(SupportStatuses.frontend.IN_PROGRESS);
    }

    if (this.supportsCompleted.length) {
      this.supportsGroupedByFrontEndStatusKeys.push(SupportStatuses.frontend.COMPLETED);
    }

    // Default selected front end status (CM)
    if (!this.selectedSupportsFrontEndStatus) {
      if (this.supportsInProgress.length) {
        this.selectedSupportsFrontEndStatus = SupportStatuses.frontend.IN_PROGRESS;
      } else if (this.supportsCompleted.length) {
        this.selectedSupportsFrontEndStatus = SupportStatuses.frontend.COMPLETED;
      } else {
        this.selectedSupportsFrontEndStatus = SupportStatuses.frontend.IN_PROGRESS;
      }
    }

    // determine which supports to display in dropdown (CM)
    if (this.selectedSupportsFrontEndStatus === SupportStatuses.frontend.IN_PROGRESS) {
      this.supportsToDisplay = this.supportsInProgress;
    } else if (this.selectedSupportsFrontEndStatus === SupportStatuses.frontend.COMPLETED) {
      this.supportsToDisplay = this.supportsCompleted;
    }

    // further filter attendance records to only the supports that are displayed (CM)
    this.filteredAttendanceRecords = this.filteredAttendanceRecords.filter(attendanceRecord => {
      return this.supportsToDisplay.some(({ _id }) => {
        return attendanceRecord.supportId === _id;
      });
    });

    // sort supports to display by name (CM)
    this.supportsToDisplay.sort((a, b) => {
      return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
    });

    this.setTable();
    this.SpinnerModal.deactivateSpinner();
  }

  private filterAttendanceRecordsByDateRange({
    attendanceRecords,
    startDate,
    endDate,
  }: {
    attendanceRecords: IAttendanceRecord[];
    startDate: Date;
    endDate: Date;
  }): IAttendanceRecord[] {
    const filteredAttendanceRecords = filter(attendanceRecords, attendanceRecord => {
      const eventDate = moment(attendanceRecord.eventDate);
      const inDateRange = eventDate.isSameOrAfter(startDate, 'day') && eventDate.isSameOrBefore(endDate, 'day');
      return inDateRange;
    });

    return filteredAttendanceRecords;
  }

  private setTable() {
    this.table = this.generateTableRows(this.studentSupports, this.filteredAttendanceRecords);
    this.uniqueDates = uniq(map(this.table, 'eventDate'));
  }

  // get info not available on attendanceRecord from Student Support: if not found returns undefined
  private getDetails(attendanceRecord, studentSupports) {
    return find(studentSupports, (studentSupport: any) => {
      const supportIdMatches = studentSupport.support.supportId === attendanceRecord.supportId;
      const studentIdMatches = studentSupport.student.studentId === attendanceRecord.studentId;
      return supportIdMatches && studentIdMatches;
    });
  }

  public filterTableByDate(startDate: Date, endDate: Date) {
    this.selectedSupportsFrontEndStatus = null; // reset support status when date ranges are changed (CM)
    this.setUi();
  }

  private generateTableRows(studentSupports, attendanceRecords) {
    if (!studentSupports || !attendanceRecords) return [];
    const rows: any = map(attendanceRecords, (record: any) => {
      // student support details correlated to attendance record
      // TODO: we should add studentSupportId to attendanceRecord schema -- once there
      // refactor this.getDetails()
      let studentSupport = this.getDetails(record, studentSupports);
      // if no studentSupport is found, its likely because the support has been removed
      if (!studentSupport) return;
      return {
        studentIdTrimmed: this.imStudentSupport.getStudentIdFrom_Id(studentSupport),
        student: studentSupport.student.lastFirst,
        support: studentSupport.support.name,
        attendanceStatus: record.status,
        eventDate: record.eventDate,
        show: true,
      };
    });
    // remove non-aligned records ie: bad data;
    return filter(rows, val => val);
  }

  // autocomplete functionality:
  private acFilter(searchText) {
    return function filterFn(studentName) {
      return includes(studentName.toLowerCase(), searchText.toLowerCase());
    };
  }

  private getFilteredTable(table) {
    if (!table) {
      console.error(`Cannot create filtered table when there's no table available`);
    }

    const filteredTable = filter(table, (row: any) => {
      // At the time of writing, there are four filters.
      // Date and attendance filters will set the row.show property
      // to true for filtered rows. So it'd suffice to have filtering
      // logic for just the two other filters. (Devin)
      if (row.show) {
        // by default include all rows
        let includeRow = true;

        includeRow = this.checkForSupportFilter(row);
        if (!includeRow) return includeRow;

        includeRow = this.checkForStudentNameFilter(row);
        if (!includeRow) return includeRow;

        return includeRow;
      }
    });

    return filteredTable;
  }

  private checkForSupportFilter(row) {
    const isFilterSet = !isEmpty(this.searchSupport);
    if (!isFilterSet) return true;
    return row.support === this.searchSupport;
  }

  private checkForStudentNameFilter(row) {
    const studentName = this.searchText && this.searchText.toLowerCase();
    if (!studentName) return true;

    return row.student.toLowerCase().includes(studentName);
  }

  onlySupportDates(date) {
    const dateAsString = moment(date).format('YYYY-MM-DD');
    return includes(this.uniqueDates, dateAsString);
  }

  // TODO: this needs tests
  filterTableByAttendanceStatus(table, checkbox) {
    const filter = this.checkboxFilters[checkbox];
    each(table, (row: any) => {
      if (filter.bool) {
        if (row.attendanceStatus === filter.value) row.show = true;
      } else {
        if (row.attendanceStatus === filter.value) row.show = false;
      }
    });
  }

  applySupportFilter(selectedSupport) {
    if (selectedSupport) {
      this.searchSupport = selectedSupport;
    } else {
      this.searchSupport = {};
    }
  }

  // called in md-autocomplete:
  getStudentNames(studentSupports, searchText) {
    const studentNames = uniq(map(studentSupports, (support: any) => support.student.lastFirst));
    const result = searchText ? filter(studentNames, this.acFilter(searchText)) : studentNames;
    return result;
  }

  // csv export functionality:
  generateCsv() {
    const filteredTable = this.getFilteredTable(this.table);
    const docType = 'text/csv;charset=utf-8';
    const currentDateAndTime = moment().format('MM-DD-YYYY hmma');
    const fileName = `SupportAttendance-${currentDateAndTime}.csv`;
    const csvString = this.AbsentStudentsListExporter.createAbsentListCsv(filteredTable);
    this.AbsentStudentsListExporter.exportCsv(csvString, fileName, docType);
  }

  public selectSupportStatus(): void {
    this.setView();
  }
}
